import { useState, useMemo } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  ShieldAlert, AlertTriangle, Clock, Award, Calendar, ExternalLink,
  Calculator, CheckCircle2, XCircle, Gift, FileText, Copy,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { fmtUsd } from "@/lib/format";

export default function Strategy() {
  const { data: lr } = useQuery({ queryKey: ["lr-tiers"], queryFn: api.lrTiers });
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: api.getSettings });
  const { data: summary } = useQuery({
    queryKey: ["lr-summary"],
    queryFn: api.lrSummary,
    refetchInterval: 15000,
  });

  const [calc, setCalc] = useState({
    net_deposit_30d: 0,
    futures_volume_30d: 0,
    monthly_net_pnl: 0,
  });
  const [calcResult, setCalcResult] = useState(null);

  const calcMut = useMutation({
    mutationFn: () => api.lrTierCalc(calc),
    onSuccess: (data) => setCalcResult(data),
  });

  const currentTierName = settings?.tier || "REGULAR";

  return (
    <div className="p-5 md:p-6 space-y-5 max-w-[1400px] mx-auto">
      {/* MISSION & VISION — Strategy flow at the very top */}
      <div
        data-testid="mission-vision-banner"
        className="border-2 border-emerald-500/40 bg-gradient-to-br from-emerald-500/[0.07] to-emerald-500/[0.02] p-5"
      >
        <div className="flex items-center gap-2 mb-3">
          <span className="text-[10px] uppercase tracking-[0.3em] text-emerald-400 font-semibold">
            ★ Misyonumuz · Vizyonumuz
          </span>
          <span className="text-[10px] uppercase tracking-widest text-zinc-500">
            · Darkex Loss Recovery Program ile saf kar üretimi
          </span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-3">
          <FlowStep n="1" title="Darkex'e Yatır"
            desc="Net Deposit + Volume tier&apos;ı yükseltir, %10-35 bonus oranını açar" />
          <FlowStep n="2" title="Darkex'te Bilinçli Zarar"
            desc="Realize kayıp >$10, pozisyon ≥5dk açık · Kasıtlı loss" />
          <FlowStep n="3" title="Karşı Borsada Hedge"
            desc="Binance/OKX&apos;te ters yön açılır → kayıp geri alınır (net=0)" />
          <FlowStep n="4" title="Bonus = SAF KAR"
            desc="Kayıp × tier oranı = çekilebilir bakiye olarak hesaba düşer" />
        </div>
        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="border border-emerald-500/20 bg-black/30 py-2">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">100$ zarar · REGULAR</div>
            <div className="font-mono text-base text-emerald-300 font-semibold">+10$ kar</div>
          </div>
          <div className="border border-emerald-500/30 bg-black/30 py-2">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">100$ zarar · VIP3</div>
            <div className="font-mono text-base text-emerald-300 font-semibold">+20$ kar</div>
          </div>
          <div className="border border-emerald-500/40 bg-emerald-500/[0.04] py-2">
            <div className="text-[10px] uppercase tracking-widest text-emerald-400">100$ zarar · PRO3</div>
            <div className="font-mono text-base text-emerald-300 font-semibold">+35$ kar</div>
          </div>
        </div>
      </div>

      {/* PROMINENT bonus claim CTA */}
      <a
        href="https://support.darkex.com/hc/en-us/requests/new"
        target="_blank"
        rel="noreferrer"
        data-testid="prominent-claim-cta"
        className="group block border-2 border-amber-400/50 bg-gradient-to-r from-amber-500/15 to-amber-400/[0.05] hover:from-amber-500/25 hover:to-amber-400/[0.1] transition-all p-5"
      >
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="flex items-center justify-center w-14 h-14 bg-amber-400/15 border border-amber-400/40 shrink-0">
              <Gift className="w-7 h-7 text-amber-300" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-amber-400 mb-1">
                🎯 Darkex Loss Bonus Başvuru
              </div>
              <div className="text-lg font-semibold text-white">
                Bonus Başvurusunu Yap — 24 Saat İçinde
              </div>
              <div className="text-xs text-zinc-400 mt-0.5">
                Destek formunda <b className="text-amber-300">&quot;Loss Bonus&quot;</b> tipini seç, Position ID&apos;yi gir, gönder.
                Onaylanan bonus <b className="text-emerald-300">çekilebilir bakiye</b> olarak hesaba düşer.
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 text-amber-300 group-hover:text-amber-200 shrink-0">
            <span className="text-sm font-semibold uppercase tracking-widest">Forma Git</span>
            <ExternalLink className="w-5 h-5 transition-transform group-hover:translate-x-0.5" />
          </div>
        </div>
      </a>

      {/* Header */}
      <div>
        <h1 className="text-xl font-semibold tracking-tight" data-testid="strategy-page-title">
          Loss Recovery Program · Strateji
        </h1>
        <p className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 mt-1">
          Darkex Resmi Kayıp Kurtarma Programı · Kurallar ve Hesaplayıcı
        </p>
      </div>

      {/* Important risk note (NOT a hedging ban — official program doesn't forbid hedging) */}
      <div
        data-testid="risk-note-banner"
        className="border border-amber-500/30 bg-amber-500/[0.04] p-4"
      >
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
          <div className="text-xs leading-relaxed text-zinc-300">
            <b className="text-amber-300">Resmi Kural:</b> Loss Recovery Program kuralları içinde &quot;hedging yasaktır&quot; <u>açıkça yazmıyor</u>.
            Yalnızca <b className="text-white">&quot;manipulative trading behavior, system abuse veya malicious activity diskalifikasyona yol açabilir&quot;</b> ifadesi var.
            Yine de Darkex risk takımı çapraz-borsa hedge&apos;i &quot;sistem suistimali&quot; olarak yorumlayabilir — son karar onlara ait.
            <span className="block mt-1.5 text-zinc-400">
              Diğer Darkex kampanyaları (50% Loss Shield, Mystery Box, Volume) hedge&apos;i <i>açıkça</i> yasaklar; karıştırmayın.
              Bu uygulama yalnızca analiz aracıdır.
            </span>
          </div>
        </div>
      </div>

      {/* Quick rule cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
        <RuleCard
          icon={Clock}
          label="Min Pozisyon Süresi"
          value={`${(lr?.rules?.min_position_hold_seconds || 300) / 60} dakika`}
          desc="Bu süreden önce kapatılan pozisyonlar bonus hesabına dahil edilmez."
        />
        <RuleCard
          icon={AlertTriangle}
          label="Min Realize Kayıp"
          value={`$${lr?.rules?.min_realized_loss_usdt || 10} USDT`}
          desc="Tek işlemde gerçekleşmiş kayıp bu eşiğin altındaysa bonus yok."
        />
        <RuleCard
          icon={Calendar}
          label="Başvuru Süresi"
          value={`${lr?.rules?.claim_window_hours || 24} saat`}
          desc="Pozisyon kapatıldıktan sonra Darkex destekten talep gönderilmeli."
        />
        <RuleCard
          icon={Gift}
          label="Bonus"
          value="Çekilebilir"
          desc="Onaylanan bonus çekilebilir bakiye olarak hesaba yatırılır."
        />
      </div>

      {/* Current tier highlight + monthly summary */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-2">
        <div className="lg:col-span-1 border border-emerald-500/30 bg-emerald-500/[0.04] p-4">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
            Mevcut Seviye (Ayarlar)
          </div>
          <div className="font-mono text-3xl font-semibold text-emerald-400 mt-1" data-testid="current-tier-display">
            {currentTierName}
          </div>
          <div className="text-xs text-zinc-400 mt-2">
            Bonus oranı: <span className="text-white font-mono">%{Math.round((settings?.default_bonus_rate || 0.1) * 100)}</span>
          </div>
        </div>
        <div className="lg:col-span-2 grid grid-cols-3 gap-2">
          <KpiCard label="Bekleyen Talep" value={summary?.pending_claims?.length || 0} testId="lr-pending-count" />
          <KpiCard
            label="Beklenen Bonus (tahmini)"
            value={fmtUsd(summary?.eligible_bonus_total)}
            accent="amber"
            testId="lr-pending-bonus"
          />
          <KpiCard
            label="Bu Ay Kazanılan (tahmini)"
            value={fmtUsd(summary?.monthly_bonus_total)}
            accent="emerald"
            testId="lr-monthly"
          />
        </div>
      </div>

      {/* Honest disclaimer — these are projections, not Darkex-confirmed */}
      <div className="flex items-start gap-2 text-[11px] text-amber-500/80 bg-amber-500/[0.04] border border-amber-500/20 rounded-sm p-2.5"
           data-testid="lr-projection-disclaimer">
        <Award className="w-3.5 h-3.5 shrink-0 mt-0.5" />
        <span>
          <b className="text-amber-400">Bu rakamlar tahminidir.</b> Bonus
          başvuruları Darkex destek ekibi tarafından <b>insan onayıyla</b>
          işlenir; bu uygulamanın Darkex'in onay durumunu otomatik sorgulama
          yetkisi yoktur. Gerçek tutarlar Darkex'in kararına göre değişebilir.
          Onaylanan tutarları Darkex panelinden takip et.
        </span>
      </div>

      {/* Pending claims (urgent) */}
      {summary?.pending_claims?.length > 0 && (
        <PendingClaims claims={summary.pending_claims} />
      )}

      {/* Tier table */}
      <div>
        <h2 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
          <Award className="w-3.5 h-3.5" /> Tier Yapısı
        </h2>
        <div className="border border-white/[0.06] overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-white/[0.02] text-[10px] uppercase tracking-widest text-zinc-500">
              <tr>
                <th className="text-left px-3 py-2 font-medium">Seviye</th>
                <th className="text-right px-3 py-2 font-medium">Bonus</th>
                <th className="text-right px-3 py-2 font-medium">Net Yatırım (30g)</th>
                <th className="text-center px-3 py-2 font-medium">·</th>
                <th className="text-right px-3 py-2 font-medium">Hacim (30g)</th>
                <th className="text-center px-3 py-2 font-medium">·</th>
                <th className="text-right px-3 py-2 font-medium">Aylık Net K/Z</th>
              </tr>
            </thead>
            <tbody>
              {(lr?.tiers || []).map((t) => {
                const isCurrent = t.name === currentTierName;
                return (
                  <tr
                    key={t.name}
                    data-testid={`tier-row-${t.name}`}
                    className={`border-t border-white/[0.04] ${isCurrent ? "bg-emerald-500/[0.04]" : "hover:bg-white/[0.015]"}`}
                  >
                    <td className="px-3 py-2 font-mono font-semibold">
                      {isCurrent && <span className="text-emerald-400 mr-1">●</span>}
                      {t.name}
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-amber-300">
                      %{t.bonus_rate_pct}
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-zinc-300">
                      {t.net_deposit_min > 0 ? `$${(t.net_deposit_min / 1000).toFixed(0)}K` : "—"}
                    </td>
                    <td className="px-3 py-2 text-center text-[10px] uppercase tracking-widest text-zinc-500">
                      {t.deposit_volume_op}
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-zinc-300">
                      {t.volume_min > 0 ? `$${(t.volume_min / 1_000_000).toFixed(0)}M` : "—"}
                    </td>
                    <td className="px-3 py-2 text-center text-[10px] uppercase tracking-widest text-zinc-500">
                      {t.pnl_op}
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-rose-300">
                      {t.pnl_threshold < 0 ? `-$${Math.abs(t.pnl_threshold / 1000).toFixed(0)}K` : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="text-[10px] text-zinc-500 mt-2 leading-relaxed">
          • Net Yatırım = son 30 gün (yatırılan − çekilen). · Hacim = son 30 gün futures hacmi.
          · Aylık Net K/Z = takvim ayının 1&apos;inden bugüne (ücretler dahil), her ayın 1&apos;inde sıfırlanır.
        </p>
      </div>

      {/* Tier calculator */}
      <div>
        <h2 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
          <Calculator className="w-3.5 h-3.5" /> Tier Hesaplayıcı
        </h2>
        <div className="border border-white/[0.06] p-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <CalcInput
              label="Net Yatırım (Son 30 gün, USDT)"
              value={calc.net_deposit_30d}
              onChange={(v) => setCalc({ ...calc, net_deposit_30d: v })}
              testId="calc-deposit"
            />
            <CalcInput
              label="Futures Hacim (Son 30 gün, USDT)"
              value={calc.futures_volume_30d}
              onChange={(v) => setCalc({ ...calc, futures_volume_30d: v })}
              testId="calc-volume"
            />
            <CalcInput
              label="Aylık Net K/Z (negatif)"
              value={calc.monthly_net_pnl}
              onChange={(v) => setCalc({ ...calc, monthly_net_pnl: v })}
              testId="calc-pnl"
              allowNegative
            />
          </div>
          <Button
            onClick={() => calcMut.mutate()}
            disabled={calcMut.isPending}
            data-testid="calc-submit"
            className="h-9 px-5 bg-white text-black hover:bg-white/90 text-xs uppercase tracking-widest font-semibold rounded-sm"
          >
            Hesapla
          </Button>
          {calcResult && (
            <div className="mt-3 pt-3 border-t border-white/5 grid grid-cols-2 gap-3" data-testid="calc-result">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">Şu anki tier</div>
                <div className="font-mono text-2xl text-emerald-400 mt-1">
                  {calcResult.current_tier} · %{calcResult.bonus_rate_pct}
                </div>
              </div>
              {calcResult.next_tier && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                    Bir sonraki ({calcResult.next_tier} · %{calcResult.next_bonus_rate_pct})
                  </div>
                  <div className="text-xs text-zinc-400 mt-1 space-y-0.5">
                    {calcResult.next_requirements?.net_deposit_needed > 0 && (
                      <div>+ ${calcResult.next_requirements.net_deposit_needed.toLocaleString()} yatırım</div>
                    )}
                    {calcResult.next_requirements?.volume_needed > 0 && (
                      <div>+ ${calcResult.next_requirements.volume_needed.toLocaleString()} hacim</div>
                    )}
                    {calcResult.next_requirements?.pnl_needed > 0 && (
                      <div>+ ${calcResult.next_requirements.pnl_needed.toLocaleString()} K/Z</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Eligible pairs */}
      <div>
        <h2 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
          <CheckCircle2 className="w-3.5 h-3.5" /> Uygun Pariteler
        </h2>
        <div className="flex flex-wrap gap-1.5">
          {(lr?.rules?.eligible_pairs || []).map((p) => (
            <span
              key={p}
              data-testid={`eligible-pair-${p}`}
              className="px-2.5 py-1 border border-white/10 bg-white/[0.02] font-mono text-[11px] text-zinc-300"
            >
              {p}
            </span>
          ))}
        </div>
      </div>

      {/* Quick rules list */}
      <div className="border border-white/[0.06] p-4">
        <h2 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
          <FileText className="w-3.5 h-3.5" /> Önemli Kurallar
        </h2>
        <ul className="text-xs text-zinc-400 space-y-1.5 list-disc pl-5">
          <li>Pozisyon en az <b className="text-white">5 dakika (300 saniye)</b> tutulmalı. Bu süreden önce kapatılırsa bonus hesabına dahil edilmez.</li>
          <li>Tek bir işlemin realize kaybı <b className="text-white">en az 10 USDT</b> olmalı. Daha az kayıp bonus dışıdır.</li>
          <li>Pozisyon kapanışından <b className="text-white">24 saat içinde</b> Darkex destekten &quot;Loss Bonus&quot; başvurusu yapılmalı.</li>
          <li>Aylık Net K/Z <b className="text-white">negatif</b> ve Net Yatırım <b className="text-white">pozitif</b> olmalı.</li>
          <li>Sadece <b className="text-white">Darkex Futures</b> (spot dahil değil).</li>
          <li>Önceki ay alınan bonuslar sonraki hesaplamadan düşülür (çift refund engellenir).</li>
          <li>Manipülatif davranış, çoklu hesap, sistem suistimali → <b className="text-rose-400">diskalifiye</b>.</li>
        </ul>
        <a
          href={lr?.support_form_url || "https://support.darkex.com/hc/en-us/requests/new"}
          target="_blank"
          rel="noreferrer"
          data-testid="support-form-link"
          className="inline-flex items-center gap-1.5 mt-3 text-xs text-emerald-400 hover:underline"
        >
          Darkex Destek Formu · &quot;Loss Bonus&quot; seç <ExternalLink className="w-3 h-3" />
        </a>
      </div>
    </div>
  );
}

const FlowStep = ({ n, title, desc }) => (
  <div className="border border-emerald-500/20 bg-black/30 px-3 py-2.5">
    <div className="flex items-center gap-1.5 mb-1">
      <span className="font-mono text-[10px] text-emerald-400 bg-emerald-500/15 px-1.5 py-0.5">{n}</span>
      <span className="text-xs font-medium text-white">{title}</span>
    </div>
    <div className="text-[11px] text-zinc-400 leading-tight">{desc}</div>
  </div>
);

const RuleCard = ({ icon: Icon, label, value, desc }) => (
  <div className="border border-white/[0.06] p-3.5 bg-white/[0.01]">
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5">
      <Icon className="w-3 h-3" /> {label}
    </div>
    <div className="font-mono text-base font-medium text-white">{value}</div>
    <div className="text-[10px] text-zinc-500 mt-1.5 leading-tight">{desc}</div>
  </div>
);

const KpiCard = ({ label, value, accent = "white", testId }) => {
  const colors = {
    white: "text-white",
    emerald: "text-emerald-400",
    amber: "text-amber-300",
  };
  return (
    <div data-testid={testId} className="border border-white/[0.06] p-4 bg-white/[0.01]">
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">{label}</div>
      <div className={`font-mono text-2xl font-medium ${colors[accent]}`}>{value}</div>
    </div>
  );
};

const CalcInput = ({ label, value, onChange, testId, allowNegative = false }) => (
  <div>
    <label className="block text-[10px] uppercase tracking-widest text-zinc-500 mb-1.5">
      {label}
    </label>
    <Input
      type="number"
      data-testid={testId}
      value={value === 0 ? "" : value}
      onChange={(e) => {
        const raw = e.target.value;
        const n = raw === "" ? 0 : Number(raw);
        if (!allowNegative && n < 0) return;
        onChange(n);
      }}
      placeholder="0"
      className="bg-black/40 border-white/10 font-mono text-sm h-10"
    />
  </div>
);

const PendingClaims = ({ claims }) => {
  const copyTicket = async (id) => {
    try {
      const data = await api.positionEligibility(id);
      await navigator.clipboard.writeText(data.claim_ticket_text);
      toast.success("Destek bileti metni kopyalandı");
    } catch (e) {
      toast.error("Kopyalama hatası");
    }
  };
  return (
    <div data-testid="pending-claims-section" className="border border-amber-400/30 bg-amber-400/[0.04] p-4">
      <h2 className="text-sm font-medium text-amber-300 mb-3 flex items-center gap-2">
        <Clock className="w-3.5 h-3.5" /> Bekleyen Bonus Başvuruları
        <span className="text-[10px] uppercase tracking-widest text-zinc-500">
          · 24 saatlik pencere yakın
        </span>
      </h2>
      <div className="space-y-2">
        {claims.map((c) => {
          const hours = c.seconds_to_deadline ? Math.floor(c.seconds_to_deadline / 3600) : 0;
          const mins = c.seconds_to_deadline ? Math.floor((c.seconds_to_deadline % 3600) / 60) : 0;
          const urgent = c.seconds_to_deadline && c.seconds_to_deadline < 3 * 3600;
          return (
            <div
              key={c.position_id}
              data-testid={`claim-${c.position_id}`}
              className="grid grid-cols-12 gap-2 items-center border border-white/5 bg-black/30 px-3 py-2"
            >
              <div className="col-span-2 font-mono text-sm">{c.symbol}</div>
              <div className="col-span-3 text-xs">
                <div className="text-zinc-500 text-[10px] uppercase tracking-widest">Kayıp</div>
                <div className="font-mono text-rose-300">${c.loss_usdt.toFixed(2)}</div>
              </div>
              <div className="col-span-3 text-xs">
                <div className="text-zinc-500 text-[10px] uppercase tracking-widest">Bonus</div>
                <div className="font-mono text-amber-300">${c.bonus_estimate_usdt.toFixed(2)}</div>
              </div>
              <div className="col-span-2 text-xs">
                <div className="text-zinc-500 text-[10px] uppercase tracking-widest">Kalan</div>
                <div className={`font-mono ${urgent ? "text-rose-400" : "text-zinc-300"}`}>
                  {hours}sa {mins}dk
                </div>
              </div>
              <div className="col-span-2 flex justify-end">
                <Button
                  onClick={() => copyTicket(c.position_id)}
                  data-testid={`copy-ticket-${c.position_id}`}
                  variant="outline"
                  className="h-7 px-2.5 text-[10px] uppercase tracking-widest border-white/10 hover:bg-white/5 rounded-sm"
                >
                  <Copy className="w-3 h-3 mr-1" /> Bilet Kopyala
                </Button>
              </div>
            </div>
          );
        })}
      </div>
      <a
        href="https://support.darkex.com/hc/en-us/requests/new"
        target="_blank"
        rel="noreferrer"
        data-testid="claim-support-link"
        className="inline-flex items-center gap-1.5 mt-3 text-xs text-emerald-400 hover:underline"
      >
        Darkex destek formuna git → &quot;Loss Bonus&quot; formunu seç <ExternalLink className="w-3 h-3" />
      </a>
    </div>
  );
};
